import { customElement, resolve, type IDisposable, IEventAggregator } from 'aurelia';
import { route } from '@aurelia/router';
import { I18N, Signals } from '@aurelia/i18n';
import template from './settings.html';
import {
  deletePop3EmailAccount,
  getPop3EmailAccount,
  getUserSettings,
  retrievePop3EmailAccountNow,
  testPop3EmailConnection,
  upsertPop3EmailAccount,
  upsertUserSetting,
} from '../services/api.service';
import {
  DEFAULT_PDF_HIGHLIGHT_COLORS,
  PDF_HIGHLIGHT_COLORS_SETTING_KEY,
  parsePdfHighlightColors,
  isPdfHighlightColor,
} from '../services/pdf-marker-colors';
import {
  type DateFormatOption,
  DATE_FORMAT_OPTIONS,
  getDateFormat,
  setDateFormat,
} from '../services/date-format.service';
import { LANGUAGE_CHANGED_CHANNEL, LANGUAGES } from '../services/languages';

export const THEME_CHANGED_CHANNEL = 'radish:theme:changed';

@route({ path: 'settings', title: 'Settings' })
@customElement({ name: 'settings-view', template })
export class SettingsView {
  public activeTab: 'general' | 'uploads' | 'ocr' | 'email' = 'general';
  public theme: 'light' | 'dark' = 'light';
  public language = 'en';
  public dateFormat: DateFormatOption = 'locale';
  public ocrDefaultLanguage = 'eng';
  public ocrSkipText = true;
  public ocrAutomaticEnabled = true;
  public ocrAutomaticBackgroundPageThreshold = 4;
  public pdfHighlightColors: string[] = [...DEFAULT_PDF_HIGHLIGHT_COLORS];
  public pdfColorError = '';
  public pdfColorsSaving = false;
  public showLangDropdown = false;
  public emailConfigured = false;
  public emailPop3Server = '';
  public emailPort = 995;
  public emailUseSsl = true;
  public emailUseStartTls = false;
  public emailUsername = '';
  public emailPassword = '';
  public emailPasswordPlaceholder = '';
  public emailClearPassword = false;
  public emailHasPassword = false;
  public emailCheckIntervalMinutes = 5;
  public emailEnabled = true;
  public emailConnectionTimeoutSeconds = 30;
  public emailLastError = '';
  public emailLastCheckedAt: string | null = null;
  public emailLastSuccessfulCheckAt: string | null = null;
  public emailError = '';
  public emailMessage = '';
  public emailSaving = false;
  public emailTesting = false;
  public emailRetrieving = false;
  public readonly dateFormatOptions = DATE_FORMAT_OPTIONS;
  public readonly languages = LANGUAGES;

  public get currentLanguage() {
    return this.languages.find(l => l.value === this.language);
  }

  public selectLanguage(lang: string): void {
    this.showLangDropdown = false;
    void this.changeLanguage(lang);
  }

  private readonly i18n: I18N = resolve(I18N);
  private readonly ea: IEventAggregator = resolve(IEventAggregator);
  private _themeSub: IDisposable | null = null;
  private _localeSub: IDisposable | null = null;

  public binding(): void {
    // Sync theme when changed externally (e.g. topbar toggle)
    this._themeSub = this.ea.subscribe(THEME_CHANGED_CHANNEL, (theme: 'light' | 'dark') => {
      this.theme = theme;
    });
    // Sync language when changed externally (e.g. topbar language picker)
    this._localeSub = this.ea.subscribe(Signals.I18N_EA_CHANNEL, () => {
      this.language = this.i18n.getLocale();
      this.updateEmailPasswordPlaceholder();
    });
  }

  public unbinding(): void {
    this._themeSub?.dispose();
    this._localeSub?.dispose();
  }

  public async loading(): Promise<void> {
    this.theme = (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') ?? 'light';
    this.language = this.i18n.getLocale();
    this.dateFormat = getDateFormat();

    try {
      const settings = await getUserSettings();
      for (const s of settings) {
        if (s.key === 'theme') this.applyTheme(s.value as 'light' | 'dark', false);
        if (s.key === 'language') this.applyLanguage(s.value, false);
        if (s.key === 'dateFormat') this.applyDateFormat(s.value as DateFormatOption);
      }
      const pdfColors = settings.find(s => s.key === PDF_HIGHLIGHT_COLORS_SETTING_KEY);
      this.pdfHighlightColors = parsePdfHighlightColors(pdfColors?.value);
      const ocrLang = settings.find(s => s.key === 'ocrDefaultLanguage');
      this.ocrDefaultLanguage = ocrLang?.value ?? 'eng';
      const ocrSkip = settings.find(s => s.key === 'ocrSkipText');
      this.ocrSkipText = (ocrSkip?.value ?? 'true') === 'true';
      const automaticOcr = settings.find(s => s.key === 'ocrAutomaticEnabled');
      this.ocrAutomaticEnabled = (automaticOcr?.value ?? 'true') === 'true';
      const backgroundThreshold = Number.parseInt(
        settings.find(s => s.key === 'ocrAutomaticBackgroundPageThreshold')?.value ?? '4',
        10,
      );
      this.ocrAutomaticBackgroundPageThreshold =
        Number.isFinite(backgroundThreshold) && backgroundThreshold >= 1
          ? backgroundThreshold
          : 4;
    } catch {
      // User not logged in or settings unavailable – use defaults
    }

    try {
      const email = await getPop3EmailAccount();
      this.applyEmailSettings(email.settings, email.configured);
    } catch {
      // User not logged in or email settings unavailable – use defaults
    }
  }

  public async toggleTheme(): Promise<void> {
    const next: 'light' | 'dark' = this.theme === 'light' ? 'dark' : 'light';
    this.applyTheme(next, true);
    await upsertUserSetting('theme', next).catch(() => {/* ignore if not logged in */});
  }

  public async changeLanguage(lang: string): Promise<void> {
    this.applyLanguage(lang, true);
    await upsertUserSetting('language', lang).catch(() => {/* ignore if not logged in */});
  }

  public async changeDateFormat(fmt: DateFormatOption): Promise<void> {
    this.applyDateFormat(fmt);
    await upsertUserSetting('dateFormat', fmt).catch(() => {/* ignore if not logged in */});
  }

  public setTab(tab: 'general' | 'uploads' | 'ocr' | 'email'): void {
    this.activeTab = tab;
  }

  public setEmailSsl(enabled: boolean): void {
    this.emailUseSsl = enabled;
    if (enabled) this.emailUseStartTls = false;
  }

  public setEmailStartTls(enabled: boolean): void {
    this.emailUseStartTls = enabled;
    if (enabled) this.emailUseSsl = false;
  }

  public async saveEmailAccount(): Promise<void> {
    if (this.emailSaving) return;
    this.emailError = '';
    this.emailMessage = '';
    this.emailSaving = true;
    try {
      const settings = await upsertPop3EmailAccount({
        pop3Server: this.emailPop3Server.trim(),
        port: Number(this.emailPort),
        useSsl: this.emailUseSsl,
        useStartTls: this.emailUseStartTls,
        username: this.emailUsername.trim(),
        password: this.emailPassword,
        clearPassword: this.emailClearPassword,
        checkIntervalMinutes: Number(this.emailCheckIntervalMinutes),
        enabled: this.emailEnabled,
        connectionTimeoutSeconds: Number(this.emailConnectionTimeoutSeconds),
      });
      this.applyEmailSettings(settings, true);
      this.emailPassword = '';
      this.emailClearPassword = false;
      this.emailMessage = this.i18n.tr('settings.email.saved');
    } catch (error) {
      this.emailError = error instanceof Error ? error.message : String(error);
    } finally {
      this.emailSaving = false;
    }
  }

  public async testEmailConnection(): Promise<void> {
    if (this.emailTesting) return;
    this.emailError = '';
    this.emailMessage = '';
    this.emailTesting = true;
    try {
      const result = await testPop3EmailConnection({
        pop3Server: this.emailPop3Server.trim(),
        port: Number(this.emailPort),
        useSsl: this.emailUseSsl,
        useStartTls: this.emailUseStartTls,
        username: this.emailUsername.trim(),
        password: this.emailPassword,
        connectionTimeoutSeconds: Number(this.emailConnectionTimeoutSeconds),
      });
      if (result.success) {
        this.emailMessage = this.i18n.tr('settings.email.connectionSucceeded');
      } else {
        this.emailError = result.error ?? this.i18n.tr('settings.email.connectionFailed');
      }
    } catch (error) {
      this.emailError = error instanceof Error ? error.message : String(error);
    } finally {
      this.emailTesting = false;
    }
  }

  public async retrieveEmailNow(): Promise<void> {
    if (this.emailRetrieving) return;
    this.emailError = '';
    this.emailMessage = '';
    this.emailRetrieving = true;
    try {
      const result = await retrievePop3EmailAccountNow();
      if (!result.success && result.failedCount === 0) {
        this.emailError = result.error ?? this.i18n.tr('settings.email.retrieveFailed');
      } else if (result.failedCount > 0) {
        this.emailError = result.error ?? this.i18n.tr('settings.email.retrievePartial');
        this.emailMessage = this.i18n.tr('settings.email.retrieved', {
          count: result.importedCount,
        });
      } else {
        this.emailMessage = this.i18n.tr('settings.email.retrieved', {
          count: result.importedCount,
        });
      }
      const email = await getPop3EmailAccount();
      this.applyEmailSettings(email.settings, email.configured);
    } catch (error) {
      this.emailError = error instanceof Error ? error.message : String(error);
    } finally {
      this.emailRetrieving = false;
    }
  }

  public async deleteEmailAccount(): Promise<void> {
    if (this.emailSaving || this.emailRetrieving) return;
    this.emailError = '';
    this.emailMessage = '';
    this.emailSaving = true;
    try {
      await deletePop3EmailAccount();
      this.applyEmailSettings({
        id: 0,
        pop3Server: '',
        port: 995,
        useSsl: true,
        useStartTls: false,
        username: '',
        hasPassword: false,
        checkIntervalMinutes: 5,
        enabled: true,
        connectionTimeoutSeconds: 30,
        lastError: null,
        lastCheckedAt: null,
        lastSuccessfulCheckAt: null,
        configured: false,
      }, false);
      this.emailPassword = '';
      this.emailMessage = this.i18n.tr('settings.email.deleted');
    } catch (error) {
      this.emailError = error instanceof Error ? error.message : String(error);
    } finally {
      this.emailSaving = false;
    }
  }

  private applyEmailSettings(
    settings: {
      id: number;
      pop3Server: string;
      port: number;
      useSsl: boolean;
      useStartTls: boolean;
      username: string;
      hasPassword: boolean;
      checkIntervalMinutes: number;
      enabled: boolean;
      connectionTimeoutSeconds: number;
      lastError?: string | null;
      lastCheckedAt?: string | null;
      lastSuccessfulCheckAt?: string | null;
      configured: boolean;
    },
    configured: boolean,
  ): void {
    this.emailConfigured = configured;
    this.emailPop3Server = settings.pop3Server;
    this.emailPort = settings.port || 995;
    this.emailUseSsl = settings.useSsl;
    this.emailUseStartTls = settings.useStartTls;
    this.emailUsername = settings.username;
    this.emailHasPassword = settings.hasPassword;
    this.updateEmailPasswordPlaceholder();
    this.emailCheckIntervalMinutes = settings.checkIntervalMinutes || 5;
    this.emailEnabled = settings.enabled;
    this.emailConnectionTimeoutSeconds = settings.connectionTimeoutSeconds || 30;
    this.emailLastError = settings.lastError ?? '';
    this.emailLastCheckedAt = settings.lastCheckedAt ?? null;
    this.emailLastSuccessfulCheckAt = settings.lastSuccessfulCheckAt ?? null;
  }

  private updateEmailPasswordPlaceholder(): void {
    this.emailPasswordPlaceholder = this.emailHasPassword
      ? this.i18n.tr('settings.email.passwordUnchanged')
      : '';
  }

  public applyTheme(theme: 'light' | 'dark', save: boolean): void {
    this.theme = theme;
    document.documentElement.setAttribute('data-theme', theme);
    if (save) localStorage.setItem('radish-theme', theme);
    this.ea.publish(THEME_CHANGED_CHANNEL, theme);
  }

  public applyLanguage(lang: string, save: boolean): void {
    this.language = lang;
    void this.i18n.setLocale(lang); // fires Signals.I18N_EA_CHANNEL automatically
    this.ea.publish(LANGUAGE_CHANGED_CHANNEL, lang);
    if (save) localStorage.setItem('radish-language', lang);
  }

  public async saveOcrLanguage(): Promise<void> {
    await upsertUserSetting('ocrDefaultLanguage', this.ocrDefaultLanguage);
  }

  public async saveOcrSkipText(): Promise<void> {
    await upsertUserSetting('ocrSkipText', this.ocrSkipText.toString());
  }

  public async saveOcrAutomaticEnabled(): Promise<void> {
    await upsertUserSetting('ocrAutomaticEnabled', this.ocrAutomaticEnabled.toString());
  }

  public async saveOcrAutomaticBackgroundPageThreshold(): Promise<void> {
    const parsedThreshold = Number(this.ocrAutomaticBackgroundPageThreshold);
    const threshold = Number.isFinite(parsedThreshold)
      ? Math.max(1, Math.floor(parsedThreshold))
      : 4;
    this.ocrAutomaticBackgroundPageThreshold = threshold;
    await upsertUserSetting('ocrAutomaticBackgroundPageThreshold', threshold.toString());
  }

  public async savePdfHighlightColors(colors: string[] = this.pdfHighlightColors): Promise<void> {
    if (this.pdfColorsSaving) return;
    if (
      colors.length !== DEFAULT_PDF_HIGHLIGHT_COLORS.length ||
      colors.some(color => !isPdfHighlightColor(color))
    ) {
      this.pdfColorError = this.i18n.tr('settings.invalidPdfHighlightColor');
      return;
    }
    const previous = this.pdfHighlightColors;
    this.pdfHighlightColors = [...colors];
    this.pdfColorError = '';
    this.pdfColorsSaving = true;
    try {
      await upsertUserSetting(PDF_HIGHLIGHT_COLORS_SETTING_KEY, JSON.stringify(colors));
    } catch (error) {
      this.pdfHighlightColors = previous;
      this.pdfColorError = error instanceof Error ? error.message : String(error);
    } finally {
      this.pdfColorsSaving = false;
    }
  }

  public async changePdfHighlightColor(index: number, event: Event): Promise<void> {
    const input = event.target;
    const color = input instanceof HTMLInputElement ? input.value.trim() : '';
    if (
      !Number.isInteger(index) || index < 0 ||
      index >= this.pdfHighlightColors.length ||
      !isPdfHighlightColor(color)
    ) {
      this.pdfColorError = this.i18n.tr('settings.invalidPdfHighlightColor');
      return;
    }

    await this.savePdfHighlightColors(this.pdfHighlightColors.map((current, currentIndex) =>
      currentIndex === index ? color : current));
    if (input instanceof HTMLInputElement) input.value = this.pdfHighlightColors[index];
  }

  public async resetPdfHighlightColors(): Promise<void> {
    await this.savePdfHighlightColors([...DEFAULT_PDF_HIGHLIGHT_COLORS]);
  }

  public applyDateFormat(fmt: DateFormatOption): void {
    this.dateFormat = fmt;
    setDateFormat(fmt);
  }
}
